import React, { useState, useEffect } from 'react';
import { Search, Plus, CheckCircle2, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { cn } from 'lib/utils';
import EquipmentRentRequestCard from 'components/cpEquipments/EquipmentRentRequestCard';
import EquipmentRentDetailsModal from 'components/cpEquipments/EquipmentRentDetailsModal';
import EquipmentRequestFormModal from 'components/cpEquipments/EquipmentRequestFormModal';
import { getallEquipment } from 'services/creatorService';

const Rentequipment = () => {
  const [equipmentDetailsOpen, setEquipmentDetailsOpen] = useState(false);
  const [equipmentDetailsData, setEquipmentDetailsData] = useState(null);
  const [selectedEquipments, setSelectedEquipments] = useState([]);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [equipments, setEquipments] = useState([]);

  useEffect(() => {
    getallEquipment({}).then((response) => {
      if (response?.data) setEquipments(response.data);
    }).catch((error) => console.error("Error fetching equipment:", error));
  }, []);

  const toggleSelection = (id) => {
    setSelectedEquipments(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleOpenEquipmentDetails = (e, equipmentId) => {
    e.stopPropagation();
    const equipment = equipments.find(item => item.equipment_id === equipmentId);
    if (equipment) {
      setEquipmentDetailsData(equipment);
      setEquipmentDetailsOpen(true);
    }
  };

  const selectedItemsData = equipments.filter(item => selectedEquipments.includes(item.equipment_id));

  const filteredCards = equipments.filter((c) => {
    const name = c.equipment?.equipment_name || c.equipment_name || "";
    return name.toLowerCase().includes(search.trim().toLowerCase());
  });

  return (
    <div className="md:p-6 min-h-screen dark:bg-dark-grey transition-colors duration-300 pb-24">
      {/* Main Container following MyEquipments style */}
      <div className="bg-[#F5F1EE] dark:bg-black-primary rounded-2xl border border-[#E8DDD3] dark:border-zinc-800 shadow-sm max-w-7xl mx-auto">

        {/* Header Section */}
        <div className="p-4 md:p-6 pb-3 border-b border-[#E8DDD3] dark:border-zinc-800">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Request For Equipment
              </h1>
              <p className="text-gray-500 dark:text-zinc-400 text-sm mt-1">
                Browse and select equipment for your production.
              </p>
            </div>

            {/* Search Control styled like MyEquipments */}
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-zinc-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name..."
                className="pl-10 bg-white dark:bg-zinc-900 border border-transparent dark:border-golden dark:text-white dark:placeholder:text-zinc-500 shadow-sm h-10 focus-visible:ring-golden"
              />
            </div>
          </div>
        </div>

        {/* Grid Section */}
        <div className="p-6 pt-4">
          {filteredCards.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCards.map((item) => {
                const isSelected = selectedEquipments.includes(item.equipment_id);
                return (
                  <div
                    key={item.equipment_id}
                    onClick={() => toggleSelection(item.equipment_id)}
                    className={cn(
                      "relative group cursor-pointer rounded-xl transition-all duration-200 transform hover:scale-[1.01]",
                      isSelected
                        ? "ring-2 ring-primary bg-primary/5 dark:ring-golden shadow-lg"
                        : "hover:shadow-md"
                    )}
                  >
                    {/* Selection Badge */}
                    <div className={cn(
                      "absolute top-3 right-3 z-20 transition-opacity duration-200",
                      isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-40"
                    )}>
                      <div className="bg-white dark:bg-black rounded-full shadow-sm">
                        <CheckCircle2 className={cn(
                          "w-6 h-6",
                          isSelected ? "text-primary dark:text-golden fill-primary/10" : "text-gray-400"
                        )} />
                      </div>
                    </div>

                    <EquipmentRentRequestCard
                      item={item}
                      isSelected={isSelected}
                      handleOpenEquipmentDetails={(id) => handleOpenEquipmentDetails(window.event, id)}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500">
              <Info size={48} className="mb-4 opacity-20" />
              {/* <p className="text-lg font-medium">No equipment found matching your search.</p> */}
              <p className="text-lg font-medium">Fetching equipment for you...</p>
            </div>
          )}
        </div>
      </div>

      {/* FLOATING SELECTION BAR - Kept fixed at the bottom of the screen */}
      {selectedEquipments.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-2xl">
          <div className="bg-white/80 dark:bg-zinc-900/90 backdrop-blur-md border border-gray-200 dark:border-zinc-700 shadow-2xl rounded-full px-6 py-3 flex items-center justify-between animate-in fade-in slide-in-from-bottom-4">
            <div className="flex items-center gap-4">
              <div className="flex -space-x-2">
                {selectedItemsData.slice(0, 3).map((item, idx) => (
                  <div key={idx} className="w-8 h-8 rounded-full border-2 border-white dark:border-zinc-900 bg-gray-200 overflow-hidden">
                    <img
                      src={item?.image_url?.trim() ? item.image_url : "/images/misc/equipment_1.png"}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "/images/misc/equipment_1.png";
                      }}
                    />
                  </div>
                ))}
                {selectedEquipments.length > 3 && (
                  <div className="w-8 h-8 rounded-full border-2 border-white dark:border-zinc-900 bg-primary text-[10px] flex items-center justify-center text-white">
                    +{selectedEquipments.length - 3}
                  </div>
                )}
              </div>
              <p className="text-sm font-medium dark:text-white">
                {selectedEquipments.length} {selectedEquipments.length === 1 ? 'item' : 'items'} selected
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelectedEquipments([])}
                className="text-sm text-gray-500 hover:text-red-500 transition-colors font-medium px-2"
              >
                Clear
              </button>
              <Button
                onClick={() => setIsRequestModalOpen(true)}
                className="bg-button-coloured dark:bg-white dark:text-black dark:hover:bg-zinc-200 rounded-full px-6 flex items-center gap-2"
              >
                Send Request <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <EquipmentRentDetailsModal
        open={equipmentDetailsOpen}
        onOpenChange={setEquipmentDetailsOpen}
        equipment={equipmentDetailsData}
      />

      <EquipmentRequestFormModal
        open={isRequestModalOpen}
        onOpenChange={setIsRequestModalOpen}
        selectedItems={selectedItemsData}
        onSuccess={() => setSelectedEquipments([])}
      />
    </div>
  );
};

export default Rentequipment;
